// Foyer glue, not a port - the pre-roll-preserving replacement for
// signage-engine/playback/Playback.tsx's single-layer rendering, per the
// port plan's §6b (Sigge's explicit, binding call: pre-rolling must be
// preserved, not traded away for the ported engine's own single-instance
// full-remount model).
//
// ## The whole trick: React's own key-based reconciliation does the hand-over
//
// This does NOT re-implement SignageSurface.tsx's `MediaLayer` (a persistent
// pair of DOM elements that a boolean flag cycles between). Instead it
// renders up to two `Layer`s each tick - one for `selectCurrentAsset`
// (always present, always `active`), one for `selectNextAsset` (a VIDEO
// item only - see below) - each KEYED BY THE ASSET'S OWN id. When "next"
// becomes "current" a tick later, the id is unchanged, so React reuses the
// SAME `Layer`/`VideoPlayer`/`ShakaPlayer` instance across the render where
// its `active` prop flips from `false` to `true` - no remount, no lost
// buffering, no re-attach. A genuinely NEW id (the ordinary case: the
// previous "next" is now playing and a fresh "next" appears) mounts fresh,
// exactly as it would with no pre-roll at all. No manual slot-tracking, no
// refs, no StrictMode-safety staging - the id IS the identity, and React's
// reconciler already gets that right.
//
// ## The one real risk, and why it is safe rather than merely tolerated
//
// `selectNextAsset` (signage-engine/machine/selectors.ts, ported verbatim)
// only ever answers "the next item within the CURRENT content playlist" - it
// has no awareness of an imminent campaign break about to interrupt first
// (confirmed by reading its implementation: a plain modulo-index walk over
// `currentContent.items`, nothing else). So the "next" this component primes
// can be WRONG - a break can start instead. This is fine, not just
// tolerated, because correctness here never depends on the guess: the active
// layer is always keyed from `selectCurrentAsset`, the machine's own real
// current state, independent of whatever was pre-rolled. A wrong guess wastes
// one decoder's worth of buffering and that specific transition cold-starts
// (identical to having no pre-roll for that one tick) - it can never produce
// a stale or wrong render. See DualLayerPlayback.test.tsx's own "a guessed
// next that turns out wrong" test for the concrete proof, which the plan's
// own §6b explicitly requires before this ships.
//
// ## Pre-roll waits for the active layer's first real frame
//
// 2026-09-28, hasselbacken/302 (Samsung QB24C), 2-for-2 reloads: every COLD
// start sat ~30s on black - `play() rejected: interrupted by a call to
// pause()`, then the 30s startup watchdog failing item 0 - while ordinary
// mid-session transitions were fine. The "recovery" a few seconds later was
// never a retry: the watchdog's FAILURE advanced the machine to item 1, the
// very item that had been pre-rolling, and it started at once.
//
// The asymmetry is decoder pressure at mount time. Mid-session, a new
// pre-roll layer is the ONLY fresh Shaka pipeline in its commit: the active
// one is already decoding. On a cold mount there is no playing layer yet,
// and this component used to mount the active AND the pre-roll layer in the
// same commit - two fresh pipelines attaching, loading and claiming a
// decoder at once, on a panel whose decoder pool is finite (the same
// failure class SignageSurface.tsx already fixed for its break edges,
// commit 05e1a89). The active layer lost: something below React (Shaka's
// Tizen stall breaker is literally `pause(); play()`, and the platform
// pauses an element whose decoder is taken) paused it mid-play(), and
// nothing ever played it again.
//
// So the pre-roll layer mounts only once the CURRENT item has reported its
// first real `playing` (`onPlaybackStarted`), which restores the mid-session
// invariant everywhere, cold start included: at most one fresh pipeline
// warming up while another is starting. Pre-roll is NOT disabled - the next
// item still primes for nearly the whole of the current item's slot; it
// just starts a second or two later. Images need no decoder, so an image
// current item unlocks pre-roll immediately, as before.
import React, { useCallback, useState } from "react";
import { useSelector } from "@xstate/react";
import {
  selectCurrentAsset,
  selectNextAsset,
  selectPlaybackDuration,
  selectElapsedTime,
  selectOverlays,
  selectIsSoundEnabled,
  selectOverlayEquality,
  selectIsDebugView,
  selectIsPlaybackCircuitBreakerOpen,
} from "../signage-engine/machine/selectors";
import type { AutoplayerMachineActorRef } from "../signage-engine/machine/autoplayerMachine";
import ImagePlayer from "../signage-engine/playback/ImagePlayer";
import VideoPlayer from "../signage-engine/playback/VideoPlayer";
import LogoOverlay from "../signage-engine/playback/LogoOverlay";
import { MediaAsset, MediaType } from "../signage-engine/types/Schedule";
import {
  AssetCompleteStatus,
  AutoplayerMachineEvent,
} from "../signage-engine/machine/events";
import { LogLevel } from "../signage-engine/types/LogEntry";
import { PlaybackErrorLevel } from "../signage-engine/types/error";
import type { DrmConfig } from "../signage-engine/types/DrmConfig";

interface DualLayerPlaybackProps {
  actorRef: AutoplayerMachineActorRef;
  send: (event: AutoplayerMachineEvent) => void;
  /**
   * PLAYER-6/Verimatrix DRM for the ACTIVE item, already resolved by the
   * caller (SignageEngineSurface, via playerSixDrm.ts) - matches
   * VideoPlayer.tsx's own doc comment on its `drm` prop: nothing resolves
   * this from inside signage-engine/ itself. Never applied to the
   * pre-rolling layer - "only the active layer has a resolution: the
   * licence was minted for the item whose turn it is... a pre-rolling
   * layer... will get its own licence when it becomes active", the same
   * rule SignageSurface.tsx's own layer pair already follows.
   */
  activeDrm?: DrmConfig;
  /**
   * The resolved playback URL for a protected item, when it differs from
   * the timeline's own copy ("the resolved URL wins: for a protected title
   * it is the rendition the licence was minted FOR" - SignageSurface.tsx's
   * own rule, unchanged here). Active layer only, same reasoning as
   * `activeDrm`.
   */
  activeSrcOverride?: string;
  /**
   * A cached local copy of the pre-rolling item, when the caller
   * (SignageEngineSurface, via localAssetCache.ts - the same on-device
   * cache SignageSurface.tsx's own MediaLayer consults for BOTH its
   * layers) found one. Unlike `activeDrm`, this genuinely does apply to
   * the pre-roll layer: a cache hit is not a per-viewing resolution the
   * way a DRM licence is, it is just a faster way to fetch the exact same
   * bytes the timeline's own URL names.
   */
  preRollSrcOverride?: string;
  /**
   * ⚠️ DIAGNOSTIC OVERRIDE, 2026-09-25 (hasselbacken/302 unattended weekend
   * A/B soak) - default `false` (the real, intended behavior: pre-roll
   * stays on, unchanged). `SignageEngineSurface.tsx` currently passes
   * `true` for the duration of this test.
   *
   * When `true`, every transition falls back to upstream nenda-autoplayer's
   * own stock full-remount-per-item model (see Playback.tsx's own header
   * comment) instead of Foyer's pre-roll - accepting a visible black gap on
   * every transition in exchange for a controlled experiment. Motivation:
   * this surface has shown an ABR-reaches-for-High-profile-then-corrects
   * flicker twice; Sigge's own autoplayer app, same panel model, running
   * right next to this one, does not. The two implementations' only
   * confirmed difference is pre-rolling itself (`ShakaPlayer.tsx` carries
   * zero ABR config either way - checked directly, near-verbatim port, no
   * restrictions/abrCapConfig anywhere) - specifically, that pre-roll fetches
   * the NEXT item's segments in the background while the CURRENT item's own
   * segments are still being fetched on the same link, which autoplayer's
   * one-decoder-at-a-time model never does. If disabling this makes the
   * flicker stop over the soak, that is a confirmed answer, not a guess.
   *
   * Meant to be reverted (the CALLER stops passing `true`) once the A/B has
   * run - not a permanent behavior change, and never to be silently kept.
   */
  disablePreRoll?: boolean;
}

const NOOP_COMPLETE = () => {};
const NOOP_ERROR = () => {};

/** One layer, positioned to fully overlap the surface, visible only when
 *  `active`. Images are never pre-rolled here (see the file header - no
 *  decoder to warm), so an inactive image Layer is never actually rendered
 *  by the caller; kept branching on `asset.type` regardless for the active
 *  case, where an image is exactly as common as a video. */
function Layer({
  asset,
  active,
  duration,
  startTime,
  isSoundEnabled,
  isDebugModeActive,
  drm,
  onPlaybackStarted,
  onPlaybackComplete,
  onError,
  onEvent,
}: {
  asset: MediaAsset;
  active: boolean;
  duration: number;
  startTime: number | undefined;
  isSoundEnabled: boolean;
  isDebugModeActive: boolean;
  /** Only ever set for the active layer - see DualLayerPlaybackProps's own
   *  doc comment on `activeDrm`. */
  drm?: DrmConfig;
  /** Active layer only: its first real frame (see the file header). */
  onPlaybackStarted?: () => void;
  onPlaybackComplete: () => void;
  onError: (error: Error, level: PlaybackErrorLevel) => void;
  onEvent?: (event: string) => void;
}) {
  return (
    <div
      data-testid="dual-layer-playback-layer"
      data-asset-id={asset.id}
      data-active={active}
      style={{
        position: "absolute",
        inset: 0,
        opacity: active ? 1 : 0,
        pointerEvents: active ? "auto" : "none",
      }}
    >
      {asset.type === MediaType.Image ? (
        active ? (
          <ImagePlayer
            src={asset.locator}
            duration={duration}
            onCompleted={onPlaybackComplete}
            onError={onError}
            isDebugModeActive={isDebugModeActive}
          />
        ) : null
      ) : (
        <VideoPlayer
          mediaAsset={asset}
          active={active}
          drm={drm}
          startTime={active ? startTime : undefined}
          isSoundEnabled={active && isSoundEnabled}
          onPlaybackStarted={onPlaybackStarted}
          onPlaybackComplete={onPlaybackComplete}
          onError={onError}
          onEvent={onEvent}
        />
      )}
    </div>
  );
}

const DualLayerPlayback: React.FC<DualLayerPlaybackProps> = ({
  actorRef,
  send,
  activeDrm,
  activeSrcOverride,
  preRollSrcOverride,
  disablePreRoll = false,
}) => {
  const currentAsset = useSelector(actorRef, selectCurrentAsset);
  const nextAsset = useSelector(actorRef, selectNextAsset);
  const duration = useSelector(actorRef, selectPlaybackDuration);
  const elapsedTime = useSelector(actorRef, selectElapsedTime);
  const overlays = useSelector(actorRef, selectOverlays, selectOverlayEquality);
  const isSoundEnabled = useSelector(actorRef, selectIsSoundEnabled);
  const isDebugModeActive = useSelector(actorRef, selectIsDebugView);
  const isPlaybackCircuitBreakerOpen = useSelector(
    actorRef,
    selectIsPlaybackCircuitBreakerOpen
  );

  // Which current item has produced its first real frame (see the file
  // header). Tracked per TURN, not per id: `startedForId` is reset during
  // render whenever the current id changes (React's "adjust state when a
  // prop changes" pattern - no post-commit effect, so there is never a
  // commit in which a stale "started" briefly mounts a pre-roll layer), and
  // a start reported for an id that is no longer current is ignored.
  const currentId = currentAsset?.id;
  const [turnId, setTurnId] = useState(currentId);
  const [startedForId, setStartedForId] = useState<string | undefined>(
    undefined
  );
  if (turnId !== currentId) {
    setTurnId(currentId);
    setStartedForId(undefined);
  }
  const handlePlaybackStarted = useCallback(() => {
    if (currentId !== undefined) setStartedForId(currentId);
  }, [currentId]);

  const handlePlaybackComplete = useCallback(() => {
    if (!currentAsset) return;
    send({
      type: "ASSET_COMPLETE",
      asset: currentAsset,
      status: AssetCompleteStatus.SUCCESS,
    });
  }, [send, currentAsset]);

  const handlePlaybackEvent = useCallback(
    (message: string) => {
      if (!currentAsset) return;
      send({
        type: "LOG",
        payload: {
          component: "Playback event",
          message,
          level: LogLevel.INFO,
          asset: currentAsset,
        },
      });
    },
    [send, currentAsset]
  );

  const handlePlaybackError = useCallback(
    (error: Error, level: PlaybackErrorLevel) => {
      switch (level) {
        case PlaybackErrorLevel.FAILURE:
          send({
            type: "ASSET_COMPLETE",
            status: AssetCompleteStatus.ERROR,
            error,
            asset: currentAsset,
          });
          break;
        case PlaybackErrorLevel.WARNING:
          send({
            type: "LOG",
            payload: {
              component: "Playback error",
              message: error.message,
              level: LogLevel.WARNING,
              asset: currentAsset,
            },
          });
          break;
      }
    },
    [send, currentAsset]
  );

  if (!currentAsset) return null;

  if (isPlaybackCircuitBreakerOpen) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "black",
        }}
      />
    );
  }

  const startTime =
    currentAsset.type === MediaType.LinearChannel ? undefined : elapsedTime;

  // Only a DIFFERENT, VIDEO asset gets pre-rolled: images need no decoder
  // (SignageSurface.tsx's own established reasoning, unchanged here), and a
  // one-item playlist's "next" is the SAME id as "current" - rendering it as
  // a second Layer would double-key and warn for a pre-roll that could never
  // do anything anyway (nothing to hand over to).
  //
  // `disablePreRoll` (see the prop's own doc comment): when set, this is
  // unconditionally `undefined`, turning every transition back into upstream
  // nenda-autoplayer's own stock full-remount-per-item model instead of
  // Foyer's pre-roll.
  const activeLayerStarted =
    currentAsset.type === MediaType.Image ||
    (turnId === currentAsset.id && startedForId === currentAsset.id);
  const preRollAsset =
    !disablePreRoll &&
    activeLayerStarted &&
    nextAsset &&
    nextAsset.id !== currentAsset.id &&
    nextAsset.type === MediaType.Video
      ? nextAsset
      : undefined;

  // "The resolved URL wins: for a protected title it is the rendition the
  // licence was minted FOR, and the timeline's copy may be the clear one" -
  // SignageSurface.tsx's own rule, unchanged here. `preRollSrcOverride` is a
  // DIFFERENT kind of override (a cache hit, not a per-viewing licence - see
  // this prop's own doc comment) but the mechanism is identical: swap the
  // locator, change nothing else about the asset's identity.
  const activeAsset = activeSrcOverride
    ? { ...currentAsset, locator: activeSrcOverride }
    : currentAsset;
  const resolvedPreRollAsset =
    preRollAsset && preRollSrcOverride
      ? { ...preRollAsset, locator: preRollSrcOverride }
      : preRollAsset;

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Layer
        key={currentAsset.id}
        asset={activeAsset}
        active
        duration={duration}
        startTime={startTime}
        isSoundEnabled={isSoundEnabled}
        isDebugModeActive={isDebugModeActive}
        drm={activeDrm}
        onPlaybackStarted={handlePlaybackStarted}
        onPlaybackComplete={handlePlaybackComplete}
        onError={handlePlaybackError}
        onEvent={handlePlaybackEvent}
      />
      {resolvedPreRollAsset && (
        <Layer
          key={resolvedPreRollAsset.id}
          asset={resolvedPreRollAsset}
          active={false}
          duration={0}
          startTime={undefined}
          isSoundEnabled={false}
          isDebugModeActive={false}
          onPlaybackComplete={NOOP_COMPLETE}
          onError={NOOP_ERROR}
        />
      )}
      {overlays &&
        Object.entries(overlays).map(([id, overlay]) =>
          overlay ? (
            <LogoOverlay
              key={id}
              src={overlay.locator}
              position={overlay.position}
            />
          ) : null
        )}
    </div>
  );
};

export default DualLayerPlayback;
